/**
 * i18n del portal — hecho a mano, sin librerías.
 *
 * DISEÑO
 * ------
 *  - `es.ts` es el diccionario CANÓNICO: de él se deriva `Messages` con
 *    `typeof`, así que `en.ts`/`pt.ts` (declarados `: Messages`) no compilan si
 *    les falta —o les sobra— una clave. El error es de compilación, no de
 *    runtime.
 *  - `messages` junta los tres con `satisfies Record<Locale, Messages>`.
 *  - Las claves son ANIDADAS y se acceden por punto (`'setup.matchName'`). El
 *    tipo `MessageKey` es la unión de todos los caminos hoja, así que el editor
 *    autocompleta y una clave inventada no compila.
 *  - Interpolación simple: `{name}` en el template, `t(key, { name })`.
 *
 * ESTADO DEL IDIOMA
 * -----------------
 * El idioma vive en un store observable de módulo (mismo patrón que el resto de
 * la app: el estado fuera de React). Los settings lo escriben al cargar y al
 * guardar; los componentes lo leen con `useT()` (ver `use-t.ts`) y se
 * re-renderizan solos, sin recargar la página.
 *
 * Este archivo NO importa React a propósito: se puede testear en Node.
 */
import { en } from './en';
import { es, type Messages } from './es';
import { pt } from './pt';

export type { Messages } from './es';

export type Locale = 'es' | 'en' | 'pt';

export const LOCALES: readonly Locale[] = ['es', 'en', 'pt'];

export const DEFAULT_LOCALE: Locale = 'es';

/** Nombre de cada idioma EN SU PROPIO idioma (así se rotula el selector). */
export const LOCALE_NAMES: Record<Locale, string> = {
  es: 'Español',
  en: 'English',
  pt: 'Português',
};

/** Tag BCP-47 para los formatos del navegador (`toLocaleTimeString`, etc.). */
export const LOCALE_TAGS: Record<Locale, string> = {
  es: 'es-AR',
  en: 'en-GB',
  pt: 'pt-BR',
};

/**
 * El `satisfies` es el que obliga: si a `en` o a `pt` les faltara una clave,
 * esta línea no compila (además del `: Messages` de cada archivo).
 */
export const messages = { es, en, pt } satisfies Record<Locale, Messages>;

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Claves con autocompletado: unión de los caminos hoja del diccionario
// ---------------------------------------------------------------------------

type Leaves<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends object
      ? `${K}.${Leaves<T[K]>}`
      : never;
}[keyof T & string];

export type MessageKey = Leaves<Messages>;

export type Vars = Record<string, string | number>;

export type TFunction = (key: MessageKey, vars?: Vars) => string;

// ---------------------------------------------------------------------------
// Resolución e interpolación
// ---------------------------------------------------------------------------

/** `'Hola {name}'` + `{ name: 'Ana' }` → `'Hola Ana'`. Lo que falta queda igual. */
export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

/** Camina el diccionario por el camino con puntos; `null` si no llega a una hoja. */
export function resolve(dictionary: unknown, key: string): string | null {
  let node: unknown = dictionary;
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return null;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : null;
}

/**
 * Texto de una clave. Si el idioma elegido no la tuviera (imposible por tipos,
 * pero el runtime igual no puede quedar en blanco) cae al español y, en última
 * instancia, devuelve la propia clave.
 */
export function translate(locale: Locale, key: MessageKey, vars?: Vars): string {
  const template = resolve(messages[locale], key) ?? resolve(messages[DEFAULT_LOCALE], key) ?? key;
  return interpolate(template, vars);
}

const translators = new Map<Locale, TFunction>();

/** Traductor de un idioma. La identidad es estable (sirve para memos de React). */
export function translator(locale: Locale): TFunction {
  const cached = translators.get(locale);
  if (cached) return cached;
  const fn: TFunction = (key, vars) => translate(locale, key, vars);
  translators.set(locale, fn);
  return fn;
}

// ---------------------------------------------------------------------------
// Store del idioma activo (observable, fuera de React)
// ---------------------------------------------------------------------------

let current: Locale = DEFAULT_LOCALE;
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

/** Cambia el idioma en caliente y avisa a la UI (no hace falta recargar). */
export function setLocale(locale: Locale): void {
  const next = isLocale(locale) ? locale : DEFAULT_LOCALE;
  if (next === current) return;
  current = next;
  for (const listener of listeners) listener();
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** Traductor del idioma activo, para los módulos puros que no reciben `t`. */
export function getT(): TFunction {
  return translator(current);
}

/** Atajo de un solo uso: `t('nav.capture')`. */
export function t(key: MessageKey, vars?: Vars): string {
  return translate(current, key, vars);
}
